// Prompt 0455 — Orquestador del panel de Rol Funcional (modal con 2 tabs).
// Responsabilidades:
//   - Layout overlay + cabecera (titulo, tabs)
//   - Estado del tab activo
//   - Cierre ESC / click-fuera con dirty-state guard (DirtyStateWarning)
//   - Pasar niveles + rol a DatosTab / FichaTab
// Patron replicado de AdminPuestoPanel (0454) adaptado a 2 tabs (Datos, Ficha).

import { useState, useEffect } from 'react';
import { useI18n } from '../../../../../../i18n';
import type { RolFuncional, NivelPuesto } from '../../../../../shared/services/api';
import { DatosTab } from './tabs/DatosTab';
import { FichaTab } from './tabs/FichaTab';
import { DirtyStateWarning } from '../ficha-shared/DirtyStateWarning';
import { useDirtyTabs } from '../ficha-shared/useDirtyTabs';
import { AdminOverlayPanel } from '../shared/AdminOverlayPanel';

export type RolFuncionalTabId = 'datos' | 'ficha';

interface AdminRolFuncionalPanelProps {
  rol: RolFuncional | null; // null = crear nuevo
  niveles: NivelPuesto[];
  initialTab?: RolFuncionalTabId; // por defecto 'datos'
  onClose: () => void;
  onSaved: () => void;
}

export function AdminRolFuncionalPanel({
  rol,
  niveles,
  initialTab = 'datos',
  onClose,
  onSaved,
}: AdminRolFuncionalPanelProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.rolFuncionalPanel ?? {}) as unknown as Record<string, string>;

  const isCrear = rol === null;
  const [currentRol, setCurrentRol] = useState<RolFuncional | null>(rol);
  const [activeTab, setActiveTab] = useState<RolFuncionalTabId>(isCrear ? 'datos' : initialTab);
  const [showDirtyWarning, setShowDirtyWarning] = useState(false);

  const dirty = useDirtyTabs<RolFuncionalTabId>();

  useEffect(() => {
    setCurrentRol(rol);
  }, [rol]);

  // Escape gestionado por AdminOverlayPanel (desde 0483). Guard de dirty-state
  // aplicado via requestClose que se pasa como onClose al overlay.

  const TABS: { key: RolFuncionalTabId; label: string }[] = [
    { key: 'datos', label: panel.tabDatos ?? 'Datos' },
    { key: 'ficha', label: panel.tabFicha ?? 'Ficha' },
  ];

  function requestClose() {
    if (dirty.anyDirty()) {
      setShowDirtyWarning(true);
    } else {
      onClose();
    }
  }

  function confirmDiscardAndClose() {
    dirty.markAllClean();
    setShowDirtyWarning(false);
    onClose();
  }

  function tabLabel(id: RolFuncionalTabId): string {
    return TABS.find((x) => x.key === id)?.label ?? id;
  }

  const tituloMostrado = currentRol?.nombre ?? panel.nuevoRol ?? 'Nuevo rol funcional';

  const fichaDisabled = isCrear && !currentRol;

  return (
    <AdminOverlayPanel open={true} onClose={requestClose}>
      <>
        {/* Cabecera */}
        <div
          style={{
            flexShrink: 0,
            padding: '24px 32px 0',
            borderBottom: '1px solid #E2E6ED',
            background: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1F3864' }}>
              {tituloMostrado}
            </h2>
          </div>

          <div style={{ display: 'flex' }}>
            {TABS.map((tab) => {
              const disabled = tab.key === 'ficha' && fichaDisabled;
              const active = activeTab === tab.key;
              const tabIsDirty = dirty.isDirty(tab.key);
              return (
                <button
                  key={tab.key}
                  disabled={disabled}
                  title={disabled ? (panel.fichaDeshabilitada ?? '') : undefined}
                  onClick={() => !disabled && setActiveTab(tab.key)}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: 'transparent',
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    color: disabled ? '#C4C9D1' : active ? '#1F3864' : '#5A6171',
                    borderBottom: active ? '2px solid #1F3864' : '2px solid transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    marginBottom: -1,
                  }}
                >
                  {tab.label}
                  {tabIsDirty && (
                    <span
                      style={{
                        marginLeft: 6,
                        color: '#DC2626',
                        fontSize: 18,
                        lineHeight: 1,
                      }}
                      aria-label="unsaved"
                    >
                      ·
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Contenido scrollable */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 32px 24px' }}>
          {activeTab === 'datos' && (
            <DatosTab
              rol={currentRol}
              niveles={niveles}
              isDirty={dirty.isDirty('datos')}
              onMarkDirty={() => dirty.markDirty('datos')}
              onMarkClean={() => dirty.markClean('datos')}
              onSaved={async (createdId) => {
                dirty.markClean('datos');
                onSaved();
                if (createdId && !currentRol) {
                  // Tras crear nuevo rol: NO cerramos, habilitamos tab Ficha,
                  // quedan en el panel con datos guardados.
                  // Reconstruimos un rol minimal con id para que FichaTab pueda
                  // pedir la ficha. onSaved() actualizara la lista en el padre,
                  // pero aqui no tenemos el rol completo — asumimos activo=true
                  // y placeholders en lo demas (el padre re-abre con el rol real
                  // si lo necesita).
                  setCurrentRol({
                    id: createdId,
                    codigo: null,
                    nombre: '',
                    descripcion: null,
                    nivelMinimo: null,
                    nivelMaximo: null,
                    fichaDocumentoId: null,
                    activo: true,
                  });
                }
              }}
            />
          )}

          {activeTab === 'ficha' && currentRol?.id && (
            <FichaTab
              rolId={currentRol.id}
              hasFicha={!!currentRol.fichaDocumentoId}
              isDirty={dirty.isDirty('ficha')}
              onMarkDirty={() => dirty.markDirty('ficha')}
              onMarkClean={() => dirty.markClean('ficha')}
              onCreated={async () => {
                // Cuando se crea la ficha FR, marcamos fichaDocumentoId para que
                // el FichaTab cambie al editor. El parent se refresca via onSaved.
                setCurrentRol((prev: RolFuncional | null) =>
                  prev ? { ...prev, fichaDocumentoId: 'created' } : prev,
                );
                onSaved();
              }}
            />
          )}
        </div>

        <DirtyStateWarning
          open={showDirtyWarning}
          tabsDirtyLabels={dirty.dirtyTabIds().map(tabLabel)}
          onConfirm={confirmDiscardAndClose}
          onCancel={() => setShowDirtyWarning(false)}
        />
      </>
    </AdminOverlayPanel>
  );
}
